/**
 *  유틸 클래스
 *  이건 new 로 생성하지말고 모든함수를 static 으로 만들자.
 */


export default class Util {
    // 날짜 포맷팅
    static formatDate(date, format = 'YYYY-MM-DD') {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');

        return format
            .replace('YYYY', year)
            .replace('MM', month)
            .replace('DD', day)
            .replace('HH', hours)
            .replace('mm', minutes)
            .replace('ss', seconds);
    }

    // 숫자 포맷팅
    static formatNumber(number, options = {}) {
        const {
            decimals = 0,
            decimalSeparator = '.',
            thousandsSeparator = ','
        } = options;

        const num = parseFloat(number);
        if (isNaN(num)) return '0';

        const parts = num.toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

        return parts.join(decimalSeparator);
    }

    // 문자열 자르기
    static truncate(text, length = 100, suffix = '...') {
        if (text.length <= length) return text;
        return text.substring(0, length) + suffix;
    }

    // HTML 이스케이프 — 본문과 속성값(따옴표 안) 양쪽에 안전하도록 따옴표까지 바꾼다
    static escapeHtml(text) {
        return String(text ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c]);
    }

    /**
     * 서식 HTML 만 남긴다 — 메시지에 <br>·<b> 같은 서식을 섞어 쓰되, 그 안에 섞여 온 회원 입력이
     * 스크립트로 실행되지 않게 한다. 허용 태그 밖은 글자만 남기고(script·iframe 류는 통째로 버림),
     * 속성은 class·style·color 와 안전한 href/src 만. 실행되지 않는 <template> 안에서 거른다.
     */
    static sanitizeHtml(html) {
        const ALLOW = Util._SANITIZE_TAGS;
        const DROP = /^(SCRIPT|STYLE|IFRAME|OBJECT|EMBED|FRAME|FRAMESET|LINK|META|BASE|FORM|INPUT|BUTTON|TEXTAREA|SELECT|SVG|MATH|TEMPLATE|NOSCRIPT)$/;
        const safeUrl = v => /^(https?:|\/(?!\/)|#|data:image\/(png|jpe?g|gif|webp);)/i.test(String(v).trim());
        const tpl = document.createElement('template');
        tpl.innerHTML = String(html ?? '');
        const walk = (parent) => {
            [...parent.childNodes].forEach(node => {
                if (node.nodeType === Node.COMMENT_NODE) { node.remove(); return; }
                if (node.nodeType !== Node.ELEMENT_NODE) return;
                const tag = node.tagName;
                if (DROP.test(tag)) { node.remove(); return; }
                walk(node);
                if (!ALLOW.has(tag)) { node.replaceWith(...node.childNodes); return; }
                [...node.attributes].forEach(a => {
                    const n = a.name.toLowerCase();
                    const ok = n === 'class' || n === 'color'
                        || (n === 'style' && !/url\s*\(|expression|javascript:/i.test(a.value))
                        || ((n === 'href' && tag === 'A') || (n === 'src' && tag === 'IMG')) && safeUrl(a.value)
                        || (n === 'alt' && tag === 'IMG') || ((n === 'colspan' || n === 'rowspan') && /^T[DH]$/.test(tag));
                    if (!ok) node.removeAttribute(a.name);
                });
                if (tag === 'A') { node.setAttribute('rel', 'noopener noreferrer'); node.setAttribute('target', '_blank'); }
            });
        };
        walk(tpl.content);
        return tpl.innerHTML;
    }

    static _SANITIZE_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'BR', 'P', 'DIV', 'SPAN', 'FONT', 'SMALL', 'BIG',
        'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'DL', 'DT', 'DD', 'MARK', 'SUB', 'SUP', 'HR', 'BLOCKQUOTE', 'PRE', 'CODE',
        'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH', 'CAPTION', 'A', 'IMG']);

    // URL 파라미터 파싱
    static parseUrlParams(url = window.location.href) {
        const params = new URLSearchParams(new URL(url).search);
        const result = {};

        for (const [key, value] of params.entries()) {
            result[key] = value;
        }

        return result;
    }

    // 객체 깊은 복사
    static deepClone(obj) {
        return JSON.parse(JSON.stringify(obj));
    }

    // 객체 병합
    static mergeObjects(...objects) {
        return objects.reduce((result, current) => {
            return { ...result, ...current };
        }, {});
    }

    // 배열 중복 제거
    static uniqueArray(array) {
        return [...new Set(array)];
    }

    // 랜덤 문자열 생성
    static generateRandomString(length = 10) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';

        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        return result;
    }

    // 디바운스

    // 스로틀
    static throttle(func, limit = 300) {
        let inThrottle;
        return function executedFunction(...args) {
            if (!inThrottle) {
                func(...args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    // 로컬 스토리지 관리 — 정본은 $j.Common.Store.local (JSON 은 getJSON/setJSON). 아래는 호환 위임.
    static setLocalStorage(key, value) { return $j.Common.Store.local.setJSON(key, value); }
    static getLocalStorage(key) { return $j.Common.Store.local.getJSON(key); }
    static removeLocalStorage(key) { $j.Common.Store.local.remove(key); return true; }

    /**
     * 줄바꿈을 <br> 로 변환한다.
     * @param str
     * @returns {*}
     */

    /**
     * jquery modal 을 띄운다.
     * 이미 div가 존재한다면 $('#abc').modal(); 이렇게 하면된다. 이함수는 ajax 로 데이터를 받아와서 모달을 그려줄때 사용한다.
     * @param html ( 모달에 들어갈 내용 )
     */
    static modal(html) {
        // div 영역 생성한다.
        const divId = 'modal-' + Math.random().toString(36).substring(2, 12);
        const $div = $('<div>', { id: divId, html: html });
        $('body').append($div);

        // 모달을 띄운다.
        $('#'+divId).modal();

        // 모달이 닫힐때 생성된 div 지우기
        $(document).on($.modal.BEFORE_CLOSE, function(event,modal){
            // if(typeof console != 'undefined' && console.log) console.log("[event] " + event.type);
            let id = $(modal.elm).attr('id');
            if( id==divId ) $('#'+divId).remove();
        });
    }


    /**
     * 현재 열린 modal 을 닫는다.
     */
    static modalClose() {
        $.modal.close();
    }

    /**
     * 공지팝업 띄우기
     * @param key
     */
    static noticePopup(key) {
        const popupKey = key;
        const isHidden = $j.Common.Store.local.get(popupKey);

        if (!isHidden) {
            $('#noticePopup').show();
        }

        $('.notice-popup__close').click(function () {
            if ($('#noMorePopup').is(':checked')) {
                $j.Common.Store.local.set(popupKey, 'true');
            }
            $('#noticePopup').hide();
        });
    }

    /**
     * 모바일 여부
     * @returns {boolean}
     */
    static isMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    /**
     * 터치와 클릭 이벤트를 모두 처리할 수 있도록 바인딩하는 함수
     * @param {string|Element|jQuery} target - 이벤트를 걸 대상 (예: '#btn')
     * @param {function} callback - 실행할 콜백 함수
     */
    static bindTouchClick(target, callback) {
        $(target).each(function() {
            var $el = $(this);
            var touched = false;

            $el.off('touchend click')
                .on('touchend click', function(e) {
                    e.preventDefault();
                    if (touched && e.type === 'click') return;
                    touched = (e.type === 'touchend');
                    if (typeof callback === 'function') callback.call(this, e);
                });
        });
    }

    /**
     * 토스트 메시지 표시
     * @param {string} icon - success/warning/error/info 등
     * @param {string} title - 메시지
     */
    static tsalert(icon, title) {
        $j.Ui.Toast.show(icon, title);
    }

    /**
     * 페이지의 실제 스크롤 위치를 다양한 컨테이너 후보에서 탐색해 반환.
     * body 가 0이면 #subPage / #subPage_re / #mainholder / #indexPage 순으로 fallback.
     */
    static getScrollTop() {
        var scroll_Pos = parseInt($('body').scrollTop());
        if (scroll_Pos == 0) scroll_Pos = document.body.scrollTop;
        if (scroll_Pos == 0 && document.getElementById("subPage"))    scroll_Pos = document.getElementById("subPage").scrollTop;
        if (scroll_Pos == 0 && document.getElementById("subPage_re")) scroll_Pos = document.getElementById("subPage_re").scrollTop;
        if (scroll_Pos == 0 && document.getElementById("mainholder")) scroll_Pos = document.getElementById("mainholder").scrollTop;
        if (scroll_Pos == 0 && document.getElementById("indexPage"))  scroll_Pos = document.getElementById("indexPage").scrollTop;
        return scroll_Pos;
    }

    /**
     * 모바일 공통 로딩 인디케이터(가로 3점) 마크업.
     * 스타일은 mobile/components/_spinner.scss, 다크 색상은 base/_dark.scss 가 담당한다.
     * 로딩 표시가 필요한 곳은 이미지 대신 항상 이걸 쓴다.
     * @param {string} [modifier=''] - 'jb-spin--sm' | 'jb-spin--lg' | 'jb-spin--on-dark' | 'jb-spin--fade'
     * @returns {string}
     */
    static spinnerHtml(modifier = '') {
        return `<span class="jb-spin ${modifier}"><i></i><i></i><i></i></span>`;
    }

    /**
     * 전체 화면 로딩 오버레이 표시/숨김 — ajax 대기 공용(업로드·발주등록·통계 등).
     * 페이지 이동(#PageMoveLoading, 웨이브)과 달리 페이드 스피너를 써 "처리 중"으로 구분한다.
     * @param {boolean} isShow - true=표시, false=숨김
     */
    static loadingOverlay(isShow) {
        const id = 'loadingOverlay';
        if (isShow) {
            if (document.getElementById(id)) return; // 중복 append 방지 (여러 곳에서 동시 호출돼도 1개)
            const html = `
<div id="${id}" style="position: fixed;top:0px;left:0px;width:100%;height:100%;z-index:var(--z-index-loading);justify-content: center;align-items: center;background-color: rgba(255, 255, 255, 0.8); display: flex">
    ${Util.spinnerHtml('jb-spin--fade')}
</div>
            `;
            $('body').append($(html));
        } else if ($('#' + id).length > 0) {
            $('#' + id).remove();
        }
    }


    /**
     * 자원 아이템 뱃지 HTML — -6차 / 발주개수 / 수주포인트.
     *
     * 회원이 '가지고 있는 물건'으로 읽히게 하는 공용 표기. 이름을 숫자에 텍스트로 이어 붙이면
     * ('-6차 7일차') '차'가 겹쳐 읽기 나쁘고, 무엇의 개수인지도 흐려진다.
     * 이름·색·아이콘 매핑은 여기 한 곳뿐이라 회원화면(/m/msix·/m/point)과
     * 관리자(/adm/order/handle)가 갈리지 않는다. 스타일은 shared/components/_res-item.scss.
     *
     * @param kind 'msix' | 'odr' | 'pt'
     * @param lg   큰 숫자 옆에 놓는 큰 사이즈
     */
    static resItem(kind, lg = false) {
        const M = {
            msix: { cls: 'is-msix', icon: 'fa-bolt',  name: '-6차' },
            odr:  { cls: 'is-odr',  icon: 'fa-heart', name: '발주개수' },
            pt:   { cls: 'is-pt',   icon: 'fa-star',  name: '수주포인트' },
        }[kind];
        if (!M) return '';

        return `<span class="res-item ${M.cls}${lg ? ' is-lg' : ''}">`
             + `<i class="fas ${M.icon}"></i>${M.name}</span>`;
    }

    /**
     * `.vibrate-button` 클래스 가진 버튼들에 진동 피드백 등록.
     * navigator.vibrate 미지원 디바이스에서는 silently no-op.
     */
    static registerVibrateButton() {
        document.querySelectorAll('.vibrate-button').forEach(function(button) {
            button.addEventListener('click', function() {
                if (navigator.vibrate) {
                    navigator.vibrate(10);
                } else {
                    console.log('Vibration not supported on this device.');
                }
            });
        });
    }

    /**
     * 알럿·확인 어댑터 — 인자 모양으로 제목/아이콘/본문/버튼/핸들러를 해석한 뒤
     * **공용 컴포넌트 `$j.Ui.Dialog` 로 넘긴다.** (구: SweetAlert2 직접 호출)
     *
     *   swalert('제목')
     *   swalert(['제목','question','본문'], onConfirm, ['네','아니오'], onDeny)
     * 제목에 `<` 가 있으면 html 슬롯으로 넘긴다 (레거시 규약 유지).
     *
     * [왜 어댑터로 남기나]
     *   호출부가 571곳(resources/js 407 · blade 151 · public/js 13)이다. 여기서 한 번
     *   갈아끼우면 호출부를 건드리지 않고 전부 새 디자인으로 넘어온다.
     *   새로 쓰는 코드는 `$j.Ui.Dialog.open/confirm/alert` 을 직접 부른다 —
     *   슬롯(rows·notes·tone)을 쓸 수 있어 위계 있는 확인창을 만들 수 있다.
     *
     * [보존한 동작]
     *   · 제목의 `\n` 줄바꿈 (53곳이 쓴다 — _dialog.scss 의 white-space:pre-line)
     *   · 버튼배열 [확인, 취소]. 두번째가 있을 때만 취소버튼이 생긴다
     *   · 닫기(✕)·백드롭으로 닫으면 어느 콜백도 부르지 않는다
     *   · 반환은 Promise (기존 호출부는 반환값을 쓰지 않는다 — 전수 확인)
     *
     * [바뀐 동작]
     *   아이콘은 **배열형으로 명시한 경우에만** 배지로 뜬다. 문자열 한 개짜리 호출은
     *   배지가 없다 — 종전에는 기본값 'warning' 이 붙어 '저장되었습니다' 에도 경고
     *   아이콘이 뜨고 있었다(모바일은 아예 null 로 죽여 아이콘이 없었다).
     */
    static swalert(param1, param2 = null, param3 = null, param4 = null) {
        let title = null;   // 제목 (텍스트)
        let text = null;    // 본문
        let html = null;    // 제목자리에 온 html
        let icon = null;    // 명시된 아이콘만 배지로 쓴다
        let func1 = null;   // confirm 핸들러
        let func2 = null;   // deny 핸들러
        let buttons = null; // 버튼 라벨 배열

        // 1번째 파라미터가 "배열" 인 경우 — [제목, 아이콘, 본문]
        if (Array.isArray(param1)) {
            title = param1[0];
            if (param1[1]) icon = param1[1];
            if (param1[2]) text = param1[2];
        } else {
            title = param1;
        }

        // 제목이 html 태그인 경우 (레거시 규약) — 제목 자리에 그대로 그린다.
        // html 슬롯(작은 회색 본문)으로 보내면 <b>·<br> 만 섞인 한 문장짜리 메시지가
        // 제목 위상을 잃는다. <h2>제목</h2><p>본문</p> 처럼 짠 것도 titleHtml 안에서
        // 태그별 타이포가 잡힌다 (shared/components/_dialog.scss).
        // 단 그대로 넣지 않고 서식만 남긴다 — 호출부가 서버 메시지(회원 이름·사유 등이 섞임)를 그대로 넘기는
        // 경우가 많아, 여기서 걸러야 그 메시지에 든 <img onerror> 같은 것이 실행되지 않는다.
        if (typeof title === 'string' && title.indexOf('<') >= 0) {
            html = Util.sanitizeHtml(title);
            title = null;
        }

        if (typeof param2 === 'function') func1 = param2;
        if (Array.isArray(param3)) buttons = param3;
        if (typeof param4 === 'function') func2 = param4;

        return $j.Ui.Dialog.open({
            tone:      Util._swalTone(icon),
            title:     title,
            desc:      text,
            titleHtml: html,
            confirm:   (buttons && buttons[0]) || '확인',
            cancel:    (buttons && buttons[1]) || null,
            onConfirm: func1,
            onCancel:  func2,
        });
    }

    /** swalert 아이콘명 → Dialog 톤. 명시되지 않았으면 배지 없음(null). */
    static _swalTone(icon) {
        if (!icon) return null;
        return {
            success:  'success',
            error:    'danger',
            warning:  'warn',
            info:     'info',
            question: 'info',
        }[icon] || 'warn';
    }

    static debounce(fn, delay=0) {
        let timer = null;
        return function () {
            const context = this, args = arguments;
            clearTimeout(timer);
            timer = setTimeout(() => {
                fn.apply(context, args);
            }, delay);
        };
    }

    /**
     * 널이나 빈값일때 기본값으로 대체 한다.
     * 하지만 주로 null 을 빈값으로 변환하여 에러를 방지하는데 활용한다.
     * @param value
     * @param defaultValue
     * @returns {*|string}
     */
    static nvl(value, defaultValue='') {
        if (value === null || value === undefined || value === "") {
            return defaultValue;
        }
        return value;
    }

    /**
     * 정수형으로 변환
     * @param val
     * @returns {number}
     */
    static int(val) {
        var ret = parseInt( val );
        if( isNaN(ret)) ret = 0;
        return ret;
    }

    static number_to_withzero(num,n) {
        var len, point, str;
        var num = num + "";
        var len = num.length;

        var gap = n - len ;
        if( gap <= 0 ){
            return num;
        }else{
            var ret = "";
            for (var i = 0 ; i < gap ; i++ ){
                ret +="0";
            }

            ret += num;
            return ret;
        }

        return str;
    }

    static pad(number, length) {
        var str = '' + number;
        while (str.length < length) {
            str = '0' + str;
        }
        return str;
    }


    /**
     * string.replace 는 한번만 치환하므로 전체를 치환하는 함수가 필요해서 만들었다.
     * @param str
     * @param searchStr
     * @param replaceStr
     * @returns {string}
     */
    static replaceAll(str, searchStr, replaceStr) {
        return str.split(searchStr).join(replaceStr);
    }

    /**
     * 템플릿에 데이터를 집어넣는다. 2-depth 변수(예: {data.val})까지 처리한다.
     * @param tpl
     * @param row
     * @returns {*}
     */
    static render_template(tpl, row) {
        Object.keys(row).forEach(function(k){

            if( row[k]==null ) {
                row[k]=Util.nvl(row[k]);
            }

            if( typeof row[k] === 'object'){
                let arr=row[k];
                Object.keys(arr).forEach((k1)=>{
                    // tpl = tpl.replace('{'+k+'.'+k1+'}',arr[k1]);
                    tpl = Util.replaceAll(tpl,'{'+k+'.'+k1+'}',arr[k1]);
                });
            }else{
                // tpl = tpl.replace('{'+k+'}',row[k]);
                tpl = Util.replaceAll(tpl,'{'+k+'}',row[k]);
            }
        });
        return tpl;
    }

    /**
     * 줄바꿈을 <br>태그로 변환
     * @param str
     * @returns {*}
     */
    static nl2br(str) {
        if(!str) return '';
        return str.replace(/\n/g, '<br>');
    }

    /**
     * 문자열에서 숫자만 추출
     * @param txt
     */
    static parseNumber(txt) {
        if(!txt) return 0;
        if( !isNaN(txt)) return parseInt(txt);
        let num = txt.replace(/\D/g, '');
        num = parseInt(num);
        return num;
    }

    /**
     * 금액을 만·억 단위로 끊어 읽기 쉽게 — 금액 입력칸 옆에 띄워 자릿수 오독을 막는 용도.
     * 158000 → '15만 8천', 356200 → '35만 6천 2백', 120000000 → '1억 2,000만', 3500 → '3천 5백', 500 → '5백'.
     * 만 미만도 천·백으로 끊어 읽는다. 백 미만 끝수만 숫자로 남긴다(6250 → '6천 2백 50'). 0·음수·숫자아님은 ''.
     * @param n 숫자 또는 '69,000' 같은 문자열
     */
    static toKoreanAmount(n) {
        const num = typeof n === 'number' ? Math.trunc(n) : Util.parseNumber(String(n ?? ''));
        if (!Number.isFinite(num) || num <= 0) return '';

        const parts = [];
        const jo = Math.floor(num / 1e12);
        const eok = Math.floor((num % 1e12) / 1e8);
        const man = Math.floor((num % 1e8) / 1e4);
        const rest = num % 1e4;
        if (jo) parts.push(jo.toLocaleString() + '조');
        if (eok) parts.push(eok.toLocaleString() + '억');
        if (man) parts.push(man.toLocaleString() + '만');
        const chun = Math.floor(rest / 1000);
        const baek = Math.floor((rest % 1000) / 100);
        const low = rest % 100;
        if (chun) parts.push(chun + '천');
        if (baek) parts.push(baek + '백');
        if (low) parts.push(String(low));
        return parts.join(' ');
    }

    static coNumber(num) {
        // 입력값에서 숫자와 하이픈만 남기기
        let value = num.replace(/[^0-9]/g, '');

        // 하이픈 추가
        if (value.length > 3 && value.length <= 5) {
            value = value.slice(0, 3) + '-' + value.slice(3);
        } else if (value.length > 5) {
            value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 10);
        }
        return value;
    }

    /**
     * 'YYYY-MM-DD HH:MM:SS' / 'YYYY-MM-DDTHH:MM:SS' → 한 줄 표시 + 시분초 파란색.
     * 시간 부분이 없으면 날짜만 반환. DataTables render 에서 사용.
     * @param {string|null} dt
     * @returns {string}
     */
    static formatDateTime(dt) {
        if (!dt) return "";
        const s = String(dt).replace("T", " ");
        const [date, time] = s.split(" ");
        if (!time) return date;
        // 시각 색은 CSS 변수로 — 관리자 다크에서 갈색(#8a5a1e)이 1.5:1 로 안 읽힌다.
        // 폴백이 현행 값이라 변수를 정의하지 않는 화면(모바일·악덕)은 그대로다.
        return `<span style="white-space:nowrap">${date} <span style="color:var(--adm-time-fg, #8a5a1e)">${time}</span></span>`;
    }

    /**
     * 전화번호에 -(하이픈) 을 넣어준다.
     * @param phone
     * @returns {*}
     */
    static phoneNumber(value) {
        if (!value) {
            return "";
        }

        value = value.replace(/[^0-9]/g, "");

        // 050 안심번호(가상번호) — 자리수 가변. 050-(4자리)-(나머지 전체, 길이제한 없음).
        // 일반 포맷은 지역번호 뒤를 8자리로 잘라 뒷자리가 사라지므로 별도 처리.
        if (value.startsWith("050")) {
            return ["050", value.substring(3, 7), value.substring(7)].filter((v) => v).join("-");
        }

        let result = [];
        let restNumber = "";

        // 지역번호와 나머지 번호로 나누기
        if (value.startsWith("02")) {
            // 서울 02 지역번호
            result.push(value.substring(0, 2));
            restNumber = value.substring(2);
        } else if (value.startsWith("1")) {
            // 지역 번호가 없는 경우
            // 1xxx-yyyy
            restNumber = value;
        } else {
            // 나머지 3자리 지역번호
            // 0xx-yyyy-zzzz
            result.push(value.substring(0, 3));
            restNumber = value.substring(3);
        }

        // 지역번호 뒤는 최대 8자리 (한국 번호 최대) — 초과 입력 시 잘라 길이 폭주 방지
        restNumber = restNumber.substring(0, 8);

        if (restNumber.length === 7) {
            // 7자리만 남았을 때는 xxx-yyyy
            result.push(restNumber.substring(0, 3));
            result.push(restNumber.substring(3));
        } else {
            result.push(restNumber.substring(0, 4));
            result.push(restNumber.substring(4));
        }

        return result.filter((val) => val).join("-");
    }

    /**
     * 전화번호를 치는 동안 3·8자리째에 -(하이픈) 을 붙인다. keyup 핸들러에서 부른다.
     * 백스페이스일 때는 붙이지 않는다 — 붙이면 대시를 지울 수 없다.
     * @param {HTMLInputElement} el
     * @param {KeyboardEvent} e
     */
    static phoneDashOnKey(el, e) {
        if (e.keyCode === 8) return;
        const len = el.value.length;
        if (len === 3 || len === 8) el.value += '-';
    }

    static getSimpleDateTime(datetime) {
        let str='';
        if (!datetime) return "";

        let dj = dayjs(datetime);

        if ( dj.format('YYYY-MM-DD')==dayjs().format('YYYY-MM-DD') ) str=dj.format('HH:mm:ss');
        else str=dj.format('YYYY-MM-DD');
        return str;
    }

    /**
     * 요일구하기
     * @param date
     */
    static getYoil(date, isShort=false) {
        const yoils = ['일','월','화','수','목','금','토'];
        const dw = dayjs(date).format('d');
        let yoil = yoils[dw]
        if( !isShort) yoil+='요일';
        return yoil;
    }

    /**
     * 소속이 삼성인지
     * @param code
     * @returns {boolean}
     */
    static isSosokSamsung(code) {
        if( code.substring(0,3)=='019' ) return true;
        return false;
    }

    static isSosokJbbuller(code) {
        if( code.substring(0,3)=='012' ) return true;
        return false;
    }

    /**
     * 상위소속 코드를 구한다.
     * @param code 현재소속코드
     * @param $type 1(본사),2(지사),3(센터)
     * @return string
     */
    static getParentSosokCode(code, type) {
        if(!code) return '';
        return code.slice(0,3*type);
    }

    /**
     * 계좌정보 문자열로부터 정보를 가져온다.
     * @param str
     * @return array
     */
    static decodeMemberBank(str) {
        if(!str) return {};
        const arr = str.split('|');
        const out = {
            'bank' : arr[0],
            'account' : arr[1],
            'actholder' : arr[2],
        };
        return out;
    }

    /**
     * 이미지 URL 을 가져온다.
     * @param path string 이미지서버상의 경로
     * @param host string 이미지호스트 주소 (https://img2.jbbuler.kr)
     * @param size string original(원본), download(다운로드주소), 300x200 (가로:300, 세로:200), 300x (가로:300), x200(세로:200)
     * @return string
     */
    static getImageUrl(path, host, size='original') {
        if(!path) return;
        let imgurl = '';
        path = path.replace('datas/','');
        if( size === 'original' ){
            imgurl = `${host}/i/${path}`;
        }else if( size === 'download' ){
            imgurl = `${host}/d/${path}`;
        }else {// 썸네일 이미지
            imgurl = `${host}/${size}/${path}`;
        }
        return imgurl;
    }

    /**
     * 렌더된 썸네일 이미지 URL 을 원본 URL 로 변환한다 (사이즈 세그먼트를 원본 프리픽스로 교체).
     * @param thumbUrl string 썸네일 URL (예: https://host/x60/path)
     * @return string
     */
    static toOriginalUrl(thumbUrl) {
        if (!thumbUrl) return thumbUrl;
        return thumbUrl.replace(/\/x\d+/, '/i');
    }

    /**
     * 소셜 아이디인지
     * @param mem_id
     * @returns {boolean}
     */
    static isSocialId(mem_id) {
        if (mem_id.startsWith('N_') || mem_id.startsWith('K_')) return true;
        return false;
    }

    static openMbscDate(id) {
        if( typeof $ === 'undefined' || typeof mobiscroll === 'undefined' ) return;
        var $el = $('#' + id);
        if( !$el.length ) return;
        if( !$el.data('mbscReady') ){
            $el.mobiscroll().datepicker({
                controls: ['calendar'],
                dateFormat: 'YYYY-MM-DD',
                locale: mobiscroll.localeKo,
                buttons: []
            });
            $el.data('mbscReady', true);
        }
        $el.mobiscroll('open');
    }

    /**
     * 현재 프로토콜과 호스트 구하기 (https://jbbuller.kr 까지만 구하기)
     * @returns {string}
     */
    static getHost() {
        return window.location.protocol + "//" + window.location.host;
    }

}
